import { memo, useMemo } from 'react';
import {
  type AllowedTags,
  type Components,
  defaultRehypePlugins,
  Streamdown,
} from 'streamdown';
import type { PluggableList, Plugin } from 'unified';

const DEFAULT_MARKDOWN_COMPONENTS = {} satisfies Components;
const DEFAULT_SANITIZE_PLUGIN = defaultRehypePlugins.sanitize as [
  Plugin,
  {
    attributes?: Record<string, string[]>;
    tagNames?: string[];
  },
];
const DEFAULT_HARDEN_OPTIONS = Array.isArray(defaultRehypePlugins.harden)
  ? ((defaultRehypePlugins.harden[1] ?? {}) as Record<string, unknown>)
  : {};
const DEFAULT_HARDEN_PLUGIN = Array.isArray(defaultRehypePlugins.harden)
  ? (defaultRehypePlugins.harden[0] as Plugin)
  : (defaultRehypePlugins.harden as Plugin);

interface LiveMarkdownProps {
  content: string;
  className?: string;
  isStreaming?: boolean;
  components?: Components;
  allowedTags?: AllowedTags;
  literalTagContent?: string[];
}

/**
 * Streaming-first markdown renderer for text parts.
 * Rich blocks such as tables/components live one layer up in MessageParts.
 */
export const LiveMarkdown = memo(function LiveMarkdown({
  content,
  className,
  isStreaming = false,
  components,
  allowedTags,
  literalTagContent,
}: LiveMarkdownProps) {
  const mergedComponents = useMemo(
    () => (components ? { ...DEFAULT_MARKDOWN_COMPONENTS, ...components } : DEFAULT_MARKDOWN_COMPONENTS),
    [components]
  );
  const rehypePlugins = useMemo(
    () => buildRehypePlugins(allowedTags),
    [allowedTags]
  );

  return (
    <Streamdown
      animated={false}
      className={className}
      components={mergedComponents}
      controls={false}
      rehypePlugins={rehypePlugins}
      isAnimating={false}
      lineNumbers={false}
      literalTagContent={literalTagContent}
      mode={isStreaming ? 'streaming' : 'static'}
      normalizeHtmlIndentation
      parseIncompleteMarkdown={isStreaming}
    >
      {content}
    </Streamdown>
  );
});

function buildRehypePlugins(
  allowedTags?: AllowedTags
): PluggableList {
  const [sanitizePlugin, sanitizeSchema] = DEFAULT_SANITIZE_PLUGIN;
  const tagNames = allowedTags
    ? [...(sanitizeSchema.tagNames ?? []), ...Object.keys(allowedTags)]
    : sanitizeSchema.tagNames;
  const attributes = allowedTags
    ? { ...(sanitizeSchema.attributes ?? {}), ...allowedTags }
    : sanitizeSchema.attributes;

  return [
    defaultRehypePlugins.raw,
    [
      sanitizePlugin,
      {
        ...sanitizeSchema,
        attributes,
        tagNames,
      },
    ],
    [
      DEFAULT_HARDEN_PLUGIN,
      {
        ...DEFAULT_HARDEN_OPTIONS,
        linkBlockPolicy: 'text-only',
      },
    ],
  ];
}
